import React, { useState, useEffect } from 'react';
import { Button, Modal, message, Upload, Progress } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import MaterialSelector from '../MaterialSelector';
import MaterialUploadModal from '../MaterialUploadModal';
import api from '../../services/api';
import './CustomImageSelector.css';

/**
 * 自定义图片选择组件
 * 集成素材选择器和文件上传功能
 */
const CustomImageSelector = ({
  imageGroup = { mediaIds: [], ossUrls: {} },
  onImageGroupChange,
  disabled = false,
  stepType = 'MESSAGE',
  maxCount = 9,
  handleFileUpload, // 保留原有的文件上传功能作为备用
  handleRemoveFile,
  previewUrls = {},
  uploading = {},
  uploadProgress = {},
  fieldKey,
  getTotalAttachmentCount,
  handleUpdatePreviewUrls
}) => {
  const [materialSelectorVisible, setMaterialSelectorVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewIndex, setPreviewIndex] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  // 拖拽状态
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // 素材上传Modal状态
  const [materialUploadVisible, setMaterialUploadVisible] = useState(false);
  const [uploadMaterialType, setUploadMaterialType] = useState('IMAGE');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // 获取租户ID
  const getTenantId = () => {
    try {
      const tenantStr = localStorage.getItem('tenant');
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        return tenant.id;
      }
    } catch (error) {
      console.error('获取租户ID失败:', error);
    }
    return null;
  };

  // 获取图片预览URL
  const getImagePreviewUrl = async (mediaId, ossUrl) => {
    console.log('getImagePreviewUrl调用:', { mediaId, ossUrl, hasLocalCache: !!imagePreviewUrls[mediaId], hasGlobalCache: !!previewUrls[mediaId] });
    
    // 优先使用本地缓存的预览URL
    if (imagePreviewUrls[mediaId]) {
      console.log('使用本地缓存预览URL:', imagePreviewUrls[mediaId]);
      return imagePreviewUrls[mediaId];
    }
    
    // 检查全局缓存但要验证是否已转换
    if (previewUrls[mediaId]) {
      const globalUrl = previewUrls[mediaId];
      if (globalUrl.startsWith('http')) {
        console.log('使用全局缓存HTTP预览URL:', globalUrl);
        return globalUrl;
      } else if (globalUrl.startsWith('oss://')) {
        console.log('全局缓存是OSS格式，需要转换:', globalUrl);
        // 继续下面的转换逻辑
        ossUrl = globalUrl;
      }
    }

    // 如果有OSS URL，转换为可访问的预览URL
    if (ossUrl) {
      if (ossUrl.startsWith('oss://')) {
        try {
          console.log('转换OSS URL:', ossUrl);
          const response = await api.get(`/media/wework/oss/preview?url=${encodeURIComponent(ossUrl)}`);
          const data = response.data;
          
          if (data.success && data.url) {
            console.log('OSS URL转换成功:', data.url);
            // 更新全局预览URLs
            if (handleUpdatePreviewUrls) {
              handleUpdatePreviewUrls({ [mediaId]: data.url });
            }
            return data.url;
          } else {
            console.warn('OSS URL转换失败:', data.message);
          }
        } catch (error) {
          console.error('获取OSS预览URL失败:', error);
        }
      } else if (ossUrl.startsWith('http')) {
        // 如果已经是HTTP URL，直接使用
        console.log('直接使用HTTP URL:', ossUrl);
        return ossUrl;
      }
    }

    console.warn('无法获取有效的预览URL');
    return null;
  };

  // 处理素材选择
  const handleMaterialSelect = async (materials) => {
    
    if (!Array.isArray(materials)) {
      materials = [materials];
    }

    // 检查总数限制
    if (getTotalAttachmentCount) {
      const currentTotal = getTotalAttachmentCount();
      if (currentTotal + materials.length > maxCount) {
        message.error(`最多只能添加${maxCount}个附件，当前已有${currentTotal}个附件`);
        return;
      }
    }

    // 检查当前图片组数量限制
    const currentImageCount = imageGroup.mediaIds.length;
    if (currentImageCount + materials.length > maxCount) {
      message.error(`图片组最多只能有${maxCount}张图片，当前已有${currentImageCount}张`);
      return;
    }

    try {
      const newMediaIds = [...imageGroup.mediaIds];
      const newOssUrls = { ...imageGroup.ossUrls };
      const newPreviewUrls = {};
      
      console.log('处理素材，当前图片组状态:', { 
        currentMediaIds: newMediaIds, 
        materialsCount: materials.length 
      });
      
      // 按照素材选择的顺序逐个处理，并根据场景获取正确的企微格式数据
      for (let i = 0; i < materials.length; i++) {
        const material = materials[i];
        let mediaId, ossUrl;
        
        console.log(`处理第${i + 1}个素材 (选择顺序):`, material);
        
        try {
          // 使用与AttachmentManager一致的API调用方式获取企微格式数据
          console.log(`为素材${material.id}获取${stepType === 'MOMENTS' ? '朋友圈' : '私聊'}场景的企微格式数据`);
          
          let weworkFormatResponse;
          // 朋友圈场景使用带场景类型的新端点，其他场景使用原有端点保证兼容性
          if (stepType === 'MOMENTS') {
            console.log(`朋友圈场景：调用新端点获取moment场景素材数据，素材ID: ${material.id}`);
            weworkFormatResponse = await api.get(`/materials/${material.id}/wework-format/moment`);
            console.log(`朋友圈场景API调用完成，响应:`, weworkFormatResponse.data);
          } else {
            console.log(`消息场景：使用原有端点保证兼容性，stepType: ${stepType}`);
            weworkFormatResponse = await api.get(`/materials/${material.id}/wework-format`);
            console.log(`消息场景API调用完成，响应:`, weworkFormatResponse.data);
          }
          
          if (weworkFormatResponse.data.success) {
            const weworkMaterial = weworkFormatResponse.data.data;
            console.log(`素材${i + 1}企微格式数据获取成功:`, weworkMaterial);
            
            // 从企微格式数据中提取图片信息 - 优先使用包含完整信息的imageInfo
            const imageData = weworkMaterial.imageInfo || weworkMaterial.image;
            mediaId = imageData?.mediaId || imageData?.media_id;
            ossUrl = imageData?.url || imageData?.pic_url;
            
            // 详细调试数据结构
            console.log(`素材${i + 1}详细数据结构调试:`, {
              weworkMaterial,
              imageData,
              imageDataKeys: imageData ? Object.keys(imageData) : 'no imageData',
              url: imageData?.url,
              pic_url: imageData?.pic_url,
              picUrl: imageData?.picUrl,
              ossUrl: imageData?.ossUrl
            });
            
            console.log(`Custom素材${i + 1}提取的数据:`, {
              imageData,
              extractedMediaId: mediaId,
              extractedOssUrl: ossUrl,
              isMediaIdValid: !(!mediaId || mediaId.startsWith('temp_'))
            });
          } else {
            console.warn(`素材${i + 1}企微格式转换失败，使用原始数据:`, weworkFormatResponse.data.message);
            // 回退到原始数据结构
            if (material.imageInfo) {
              mediaId = material.imageInfo.mediaId || material.imageInfo.media_id;
              ossUrl = material.imageInfo.url || material.imageInfo.pic_url;
            } else if (material.image) {
              mediaId = material.image.mediaId || material.image.media_id;
              ossUrl = material.image.url || material.image.pic_url;
            } else {
              mediaId = material.mediaId || material.media_id;
              ossUrl = material.url || material.pic_url || material.ossUrl;
            }
          }
        } catch (error) {
          console.warn('API调用失败，回退到原始素材数据:', error);
          // 回退到原始数据结构
          if (material.imageInfo) {
            mediaId = material.imageInfo.mediaId || material.imageInfo.media_id;
            ossUrl = material.imageInfo.url || material.imageInfo.pic_url;
          } else if (material.image) {
            mediaId = material.image.mediaId || material.image.media_id;
            ossUrl = material.image.url || material.image.pic_url;
          } else {
            mediaId = material.mediaId || material.media_id;
            ossUrl = material.url || material.pic_url || material.ossUrl;
          }
        }

        // 检查是否有有效的mediaId
        if (!mediaId || mediaId.startsWith('temp_')) {
          const materialName = material.name || '未命名素材';
          message.warning(`素材"${materialName}"尚未上传完毕，请稍后刷新重试`);
          continue; // 跳过这个素材，继续处理下一个
        }

        console.log(`素材${i + 1}最终处理结果:`, { 
          originalIndex: i, 
          mediaId, 
          ossUrl,
          materialName: material.name || '未命名',
          sceneType: stepType === 'MOMENTS' ? 'moment' : 'message'
        });

        if (mediaId) {
          // 严格按照选择顺序添加到数组末尾
          newMediaIds.push(mediaId);
          newOssUrls[mediaId] = ossUrl || '';  // 即使ossUrl为空也添加，保持数据结构一致
          
          console.log(`添加到位置 ${newMediaIds.length - 1}:`, { mediaId, ossUrl });
          
          // 立即预加载预览URL并缓存（如果有ossUrl的话）
          if (ossUrl) {
            try {
              const previewUrl = await getImagePreviewUrl(mediaId, ossUrl);
              if (previewUrl) {
                newPreviewUrls[mediaId] = previewUrl;
                console.log(`素材${i + 1}预览URL获取成功:`, previewUrl);
              }
            } catch (error) {
              console.error(`素材${i + 1}预加载预览URL失败:`, error);
            }
          } else {
            console.warn(`素材${i + 1}没有ossUrl，将尝试从后端获取:`, { mediaId, material });
          }
        } else {
          console.warn(`素材${i + 1}缺少mediaId:`, { mediaId, ossUrl, material });
        }
      }
      
      console.log('最终添加顺序检查:', {
        inputOrder: materials.map((m, i) => `${i + 1}. ${m.name || m.id || '未知'}`),
        outputOrder: newMediaIds.map((id, i) => `${i + 1}. ${id}`)
      });

      // 更新组件内的预览URL缓存
      if (Object.keys(newPreviewUrls).length > 0) {
        setImagePreviewUrls(prev => ({ ...prev, ...newPreviewUrls }));
      }

      // 更新图片组
      const newImageGroup = {
        mediaIds: newMediaIds,
        ossUrls: newOssUrls
      };

      console.log('最终图片组数据:', newImageGroup);
      onImageGroupChange(newImageGroup);
      message.success(`成功添加${materials.length}张图片`);
    } catch (error) {
      console.error('处理素材选择失败:', error);
      message.error('添加图片失败，请重试');
    }

    setMaterialSelectorVisible(false);
  };

  // 处理图片删除 - 使用index简化版本
  const handleRemoveImage = (mediaId, index) => {
    console.log('开始删除图片:', { 
      mediaId, 
      index,
      totalImages: imageGroup.mediaIds.length,
      currentMediaIds: imageGroup.mediaIds 
    });
    
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除第${index + 1}张图片吗？`,
      onOk: () => {
        const newMediaIds = [...imageGroup.mediaIds];
        const newOssUrls = { ...imageGroup.ossUrls };
        
        // 根据索引删除
        const removedMediaId = newMediaIds.splice(index, 1)[0];
        console.log('删除的mediaId:', removedMediaId, '从位置:', index);
        
        // 检查是否还有其他图片使用相同的mediaId
        const stillHasSameMediaId = newMediaIds.includes(removedMediaId);
        
        // 如果没有其他图片使用相同mediaId，则删除URL映射
        if (!stillHasSameMediaId && removedMediaId) {
          delete newOssUrls[removedMediaId];
          console.log('删除URL映射:', removedMediaId);
        }

        // 清理组件内的预览URL缓存
        setImagePreviewUrls(prev => {
          const newPreviewUrls = { ...prev };
          if (!stillHasSameMediaId && removedMediaId) {
            delete newPreviewUrls[removedMediaId];
          }
          return newPreviewUrls;
        });

        const newImageGroup = {
          mediaIds: newMediaIds,
          ossUrls: newOssUrls
        };

        console.log('删除操作详情:', {
          删除前: imageGroup.mediaIds.map((id, i) => `${i + 1}.${id.slice(-6)}`),
          删除的位置: index + 1,
          删除的ID: removedMediaId.slice(-6),
          删除后: newMediaIds.map((id, i) => `${i + 1}.${id.slice(-6)}`)
        });

        // 立即更新状态
        onImageGroupChange(newImageGroup);
        
        // 调用原始的删除处理函数
        if (handleRemoveFile) {
          handleRemoveFile(fieldKey, stepType, removedMediaId);
        }

        message.success(`成功删除第${index + 1}张图片`);
      },
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true }
    });
  };

  // 处理图片预览 - 调试版本
  const handlePreview = async (mediaId, index) => {
    console.log('🔍 预览调试 - 开始:', { 
      mediaId: mediaId.slice(-8), 
      index,
      totalImages: imageGroup.mediaIds.length 
    });
    
    const ossUrl = imageGroup.ossUrls[mediaId];
    console.log('🔍 预览调试 - OSS URL:', ossUrl);
    
    if (!ossUrl) {
      console.error('❌ 图片URL不存在:', mediaId);
      message.error('预览失败，图片URL不存在');
      return;
    }
    
    // 检查缓存
    const cachedPreview = imagePreviewUrls[mediaId];
    const globalPreview = previewUrls[mediaId];
    console.log('🔍 预览调试 - 缓存状态:', {
      hasComponentCache: !!cachedPreview,
      hasGlobalCache: !!globalPreview,
      componentCacheUrl: cachedPreview,
      globalCacheUrl: globalPreview
    });
    
    let previewUrl = cachedPreview || globalPreview;
    
    // 检查预览URL是否需要转换
    if (previewUrl && previewUrl.startsWith('oss://')) {
      console.log('🔄 预览调试 - 缓存的URL是OSS格式，需要通过API获取真实预览URL');
      try {
        const response = await api.get(`/media/wework/oss/preview?url=${encodeURIComponent(previewUrl)}`);
        const data = response.data;
        
        if (data.success && data.url) {
          previewUrl = data.url;
          console.log('✅ API返回的真实预览URL:', previewUrl);
        } else {
          console.error('❌ API获取预览URL失败:', data.message);
          previewUrl = null;
        }
      } catch (error) {
        console.error('❌ 调用预览API异常:', error);
        previewUrl = null;
      }
    } else if (!previewUrl) {
      console.log('🔍 预览调试 - 没有缓存，处理OSS URL');
      if (ossUrl.startsWith('http')) {
        previewUrl = ossUrl;
        console.log('✅ 使用HTTP URL:', previewUrl);
      } else if (ossUrl.startsWith('oss://')) {
        console.log('🔄 调用API获取OSS预览URL');
        try {
          const response = await api.get(`/media/wework/oss/preview?url=${encodeURIComponent(ossUrl)}`);
          const data = response.data;
          
          if (data.success && data.url) {
            previewUrl = data.url;
            console.log('✅ API返回的真实预览URL:', previewUrl);
          } else {
            console.error('❌ API获取预览URL失败:', data.message);
            previewUrl = null;
          }
        } catch (error) {
          console.error('❌ 调用预览API异常:', error);
          previewUrl = null;
        }
      } else {
        previewUrl = ossUrl;
        console.log('⚠️ 直接使用原始URL:', previewUrl);
      }
    }
    
    console.log('🎯 最终预览URL:', previewUrl);
    
    if (previewUrl) {
      setPreviewImage(previewUrl);
      setPreviewIndex(index);
      setPreviewVisible(true);
      console.log('✅ 预览Modal已打开');
    } else {
      console.error('❌ 无法获取有效的预览URL');
      message.error('无法获取图片预览');
    }
  };

  // 拖拽：开始
  const handleDragStart = (e, index) => {
    if (disabled) return;
    setDragIndex(index);
    setDragOverIndex(null);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    } catch (_) {}
  };

  // 拖拽：进入目标上方
  const handleDragOver = (e, index) => {
    if (disabled) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  // 拖拽：离开目标
  const handleDragLeave = (e, index) => {
    if (disabled) return;
    // 仅在当前离开的就是标记的目标时重置，避免闪烁
    if (dragOverIndex === index) setDragOverIndex(null);
  };

  // 拖拽：放下
  const handleDrop = (e, toIndex) => {
    if (disabled) return;
    e.preventDefault();
    let fromIndex = dragIndex;
    try {
      const data = e.dataTransfer.getData('text/plain');
      const parsed = parseInt(data, 10);
      if (!Number.isNaN(parsed)) fromIndex = parsed;
    } catch (_) {}
    if (fromIndex === null || fromIndex === undefined) return;
    if (fromIndex === toIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newMediaIds = [...imageGroup.mediaIds];
    const [moved] = newMediaIds.splice(fromIndex, 1);
    newMediaIds.splice(toIndex, 0, moved);

    const newImageGroup = {
      mediaIds: newMediaIds,
      ossUrls: { ...imageGroup.ossUrls }
    };
    onImageGroupChange(newImageGroup);

    setDragIndex(null);
    setDragOverIndex(null);
  };

  // 拖拽：结束
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // 备用：处理文件上传（如果需要支持直接上传）
  const handleFileSelect = async (files) => {
    if (!handleFileUpload) {
      message.error('上传功能不可用，请使用素材库选择图片');
      return;
    }

    const fileList = Array.from(files);
    
    // 检查文件数量
    if (getTotalAttachmentCount) {
      const currentTotal = getTotalAttachmentCount();
      if (currentTotal + fileList.length > maxCount) {
        message.error(`最多只能添加${maxCount}个附件`);
        return;
      }
    }

    for (const file of fileList) {
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        message.error(`文件 "${file.name}" 不是图片格式`);
        continue;
      }

      // 验证文件大小
      if (file.size > 10 * 1024 * 1024) {
        message.error(`文件 "${file.name}" 大小超过10MB`);
        continue;
      }

      // 标记正在上传
      setUploadingFiles(prev => new Set([...prev, file.name]));

      try {
        const result = await handleFileUpload({
          file,
          onSuccess: (uploadResult) => {
            // 添加到图片组
            const newMediaIds = [...imageGroup.mediaIds, uploadResult.mediaId];
            const newOssUrls = { 
              ...imageGroup.ossUrls, 
              [uploadResult.mediaId]: uploadResult.ossUrl 
            };
            
            onImageGroupChange({
              mediaIds: newMediaIds,
              ossUrls: newOssUrls
            });

            setUploadingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(file.name);
              return newSet;
            });
          },
          onError: (error) => {
            console.error('上传失败:', error);
            message.error(`上传失败: ${error.message}`);
            setUploadingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(file.name);
              return newSet;
            });
          },
          fieldKey,
          mediaType: 'image',
          stepType
        });
      } catch (error) {
        console.error('文件上传异常:', error);
        setUploadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(file.name);
          return newSet;
        });
      }
    }
  };

  // 状态管理：每个图片的预览URL
  const [imagePreviewUrls, setImagePreviewUrls] = useState({});

  // 预加载图片预览URL
  useEffect(() => {
    const loadPreviewUrls = async () => {
      const newPreviewUrls = {};
      
      for (const mediaId of imageGroup.mediaIds) {
        const ossUrl = imageGroup.ossUrls[mediaId];
        console.log('预加载检查:', { mediaId, ossUrl, hasCache: !!imagePreviewUrls[mediaId] });
        
        // 检查是否已经有缓存（本地或全局）
        const hasLocalCache = imagePreviewUrls[mediaId];
        const hasGlobalCache = previewUrls[mediaId] && previewUrls[mediaId].startsWith('http');
        
        if (ossUrl && !hasLocalCache && !hasGlobalCache) {
          try {
            console.log('开始预加载图片URL:', mediaId);
            const previewUrl = await getImagePreviewUrl(mediaId, ossUrl);
            if (previewUrl) {
              console.log('预加载成功:', { mediaId, previewUrl });
              newPreviewUrls[mediaId] = previewUrl;
            }
          } catch (error) {
            console.error('预加载图片URL失败:', error);
          }
        }
      }
      
      if (Object.keys(newPreviewUrls).length > 0) {
        console.log('更新预览URL缓存:', newPreviewUrls);
        setImagePreviewUrls(prev => {
          const updated = { ...prev, ...newPreviewUrls };
          console.log('预览URL缓存更新完成，应该触发重新渲染:', updated);
          return updated;
        });
      }
    };

    if (imageGroup.mediaIds.length > 0) {
      loadPreviewUrls();
    }
  }, [imageGroup.mediaIds, imageGroup.ossUrls]);

  // 渲染图片卡片
  const renderImageCards = () => {
    console.log('开始渲染图片卡片，当前mediaIds顺序:', imageGroup.mediaIds.map((id, i) => `${i + 1}.${id.slice(-6)}`));
    
    return imageGroup.mediaIds.map((mediaId, index) => {
      const ossUrl = imageGroup.ossUrls[mediaId];
      // 获取有效的预览URL
      let previewUrl = imagePreviewUrls[mediaId] || previewUrls[mediaId];
      
      // 检查预览URL是否可用
      if (previewUrl && previewUrl.startsWith('oss://')) {
        // 如果缓存的URL是OSS格式，表示还没转换成功，暂时不显示
        previewUrl = null;
      }
      
      // 如果没有可用的预览URL，尝试处理OSS URL
      if (!previewUrl && ossUrl) {
        if (ossUrl.startsWith('http')) {
          // 如果是HTTP URL，直接使用
          previewUrl = ossUrl;
        } else {
          // 对于OSS URL，不能直接使用API endpoint作为src
          // 需要等待异步加载完成，这里暂时显示加载状态
          previewUrl = null;
        }
      }
      
      console.log(`渲染图片卡片 位置${index + 1}:`, { 
        mediaId: mediaId.slice(-6), 
        hasPreview: !!previewUrl,
        arrayPosition: index 
      });
      
      const isDragging = dragIndex === index;
      const isDragOver = dragOverIndex === index;
      return (
        <div 
          key={index}
          className="custom-image-card"
          data-array-index={index}
          data-media-id={mediaId}
          style={{
            width: '104px',
            height: '104px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            overflow: 'hidden',
            position: 'relative',
            background: '#fafafa',
            display: 'inline-block',
            marginRight: '8px',
            marginBottom: '8px'
          }}
          draggable={!disabled}
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragLeave={(e) => handleDragLeave(e, index)}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          
          // 根据拖拽态添加样式类
          {...(isDragging ? { className: 'custom-image-card dragging' } : isDragOver ? { className: 'custom-image-card drag-over' } : {})}
        >
          {/* 位置指示器 */}
          <div style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            fontSize: '12px',
            padding: '2px 6px',
            borderRadius: '4px',
            zIndex: 5,
            fontWeight: 'bold'
          }}>
            {index + 1}
          </div>
          
          {previewUrl ? (
            <>
              <img 
                src={previewUrl}
                alt={`图片${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  cursor: 'pointer'
                }}
                onClick={() => handlePreview(mediaId, index)}
                onLoad={(e) => {
                  console.log(`图片${index + 1}加载成功:`, previewUrl);
                }}
                onError={(e) => {
                  console.error(`图片${index + 1}加载失败:`, previewUrl);
                  // 隐藏失败的图片
                  e.target.style.display = 'none';
                  // 显示错误提示
                  const errorDiv = e.target.nextElementSibling?.nextElementSibling;
                  if (errorDiv) {
                    errorDiv.style.display = 'flex';
                  }
                  // 操作按钮遮罩保持可用，这样用户可以删除失败的图片
                }}
              />
              {/* 操作按钮遮罩 */}
              <div 
                className="image-card-mask"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s'
                }}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  style={{ color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(mediaId, index);
                  }}
                />
                {!disabled && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ color: 'white' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(mediaId, index);
                    }}
                  />
                )}
              </div>
              {/* 错误显示 */}
              <div 
                className="image-error-placeholder"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  flexDirection: 'column',
                  background: '#f5f5f5',
                  border: '1px dashed #d9d9d9',
                  zIndex: 1
                }}
              >
                <div style={{ fontSize: '12px', textAlign: 'center' }}>
                  图片加载失败<br/>
                </div>
              </div>
            </>
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              flexDirection: 'column'
            }}>
              <LoadingOutlined style={{ fontSize: '16px', marginBottom: '4px' }} />
              <div style={{ fontSize: '12px' }}>加载中...</div>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="custom-image-selector">
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '8px',
        alignItems: 'flex-start'
      }}>
        {/* 现有图片 */}
        {renderImageCards()}
        
        {/* 添加图片按钮 */}
        {!disabled && imageGroup.mediaIds.length < maxCount && (
          <div 
            className="custom-add-button"
            style={{
              width: '104px',
              height: '104px',
              border: '1px dashed #d9d9d9',
              borderRadius: '6px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: '#fafafa',
              transition: 'all 0.3s',
              color: '#666'
            }}
            onClick={() => {
              console.log('🔧 点击添加图片按钮，打开素材选择器');
              setMaterialSelectorVisible(true);
            }}
          >
            <PlusOutlined style={{ fontSize: '16px', marginBottom: '4px' }} />
            <div style={{ fontSize: '12px', textAlign: 'center', lineHeight: '1.2' }}>
              从素材库<br />添加
            </div>
          </div>
        )}
      </div>
      
      {/* 图片统计信息 */}
      {imageGroup.mediaIds.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          已选择 {imageGroup.mediaIds.length} 张图片
          {stepType === 'MOMENTS' && <span>（朋友圈最多9张）</span>}
        </div>
      )}

      {/* 素材选择器 */}
      <MaterialSelector
        visible={materialSelectorVisible}
        onCancel={() => {
          console.log('🔧 取消素材选择器');
          setMaterialSelectorVisible(false);
        }}
        onSelect={handleMaterialSelect}
        materialType="IMAGE"
        multiSelect={true}
        title="选择图片素材"
        allowUpload={true}
        onUpload={(selectedMaterialType) => {
          setUploadMaterialType(selectedMaterialType || 'IMAGE');
          setMaterialUploadVisible(true);
        }}
        refreshTrigger={refreshTrigger}
        tenantId={getTenantId()}
      />
      
      {/* 素材上传Modal */}
      <MaterialUploadModal
        visible={materialUploadVisible}
        onCancel={() => setMaterialUploadVisible(false)}
        onSuccess={() => {
          // 上传成功后关闭Modal并刷新素材列表
          setMaterialUploadVisible(false);
          // 触发MaterialSelector刷新
          setRefreshTrigger(prev => prev + 1);
        }}
        initialType={uploadMaterialType}
        hideTypeSelection={true}
        tenantId={getTenantId()}
      />

      {/* 图片预览Modal */}
      <Modal
        visible={previewVisible}
        title={`图片预览 第${previewIndex + 1}张（共${imageGroup.mediaIds.length}张）`}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        centered
      >
        <img 
          alt="预览图片" 
          style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }} 
          src={previewImage}
          onError={(e) => {
            console.error('预览图片加载失败:', previewImage);
            e.target.onerror = null;
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCA2NCA0MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgb3BhY2l0eT0iMC4zIj4KPHBhdGggZD0iTTMyIDQwLjVDNDkuNjczIDQwLjUgNjQgMzEuNDM1IDY0IDIwQzY0IDguNTY1IDQ5LjY3MyAwIDMyIDBDMTQuMzI3IDAgMCA4LjU2NSAwIDIwQzAgMzEuNDM1IDE0LjMyNyA0MC41IDMyIDQwLjVaIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMS4zMzMzIDI2LjY2NjdDMjMuOTEzMyAyNi42NjY3IDI2IDI0LjU4IDI2IDIyQzI2IDE5LjQyIDIzLjkxMzMgMTcuMzMzMyAyMS4zMzMzIDE3LjMzMzNDMTguNzUzMyAxNy4zMzMzIDE2LjY2NjcgMTkuNDIgMTYuNjY2NyAyMkMxNi42NjY3IDI0LjU4IDE4Ljc1MzMgMjYuNjY2NyAyMS4zMzMzIDI2LjY2NjdaIiBmaWxsPSIjQjVCNUI1Ii8+CjxwYXRoIGQ9Ik0xMC42NjY3IDMyTDIxLjMzMzMgMjEuMzMzM0wzNCAzNCIgc3Ryb2tlPSIjQjVCNUI1IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L2c+Cjwvc3ZnPgo=';
          }}
        />
      </Modal>
    </div>
  );
};

export default CustomImageSelector;
